import React, { useState, useEffect } from 'react';
import { Typography } from '../../interfaces/typography';
import { Phone, Plus, Edit2, Trash2, Loader2, Settings } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store';
import { fetchInboundRoutes, deleteInboundRoute } from '../../lib/redux/routes/slice';
import { fetchPhoneNumbers } from '../../lib/redux/phone-numbers/slice';
import { updateTeam } from '../../lib/redux/users/slice';
import { useUserData } from '../../lib/redux/users/user-data-hook';
import { InboundRoute } from '../../interfaces/bot-config';
import { RouteInlineEditor, RouteAdderRow } from './RouteInlineEditor';
import { getServicesManager } from '../../services';
import { useNavigate } from 'react-router-dom';

export function RoutingManagement() {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { allUsers, team, sortedUserGroupInfo } = useUserData();
  const { routes: allRoutes, isLoading, error } = useSelector((state: RootState) => state.inboundRoutes);
  const { phoneNumbers } = useSelector((state: RootState) => state.phoneNumbers);
  const [isAdding, setIsAdding] = useState(false);
  const [editingRoute, setEditingRoute] = useState<InboundRoute | null>(null);
  const [deletingRouteId, setDeletingRouteId] = useState<string | null>(null);
  const [isTogglingRouting, setIsTogglingRouting] = useState(false);

  useEffect(() => {
    if (team?.team_id) {
      // Set the team ID in services manager so number purchasing works
      getServicesManager().updateSelectedTeamId(team.team_id);
      dispatch(fetchInboundRoutes());
      dispatch(fetchPhoneNumbers());
    }
  }, [dispatch, team?.team_id]);


  // Filter routes to only show USER agent types or IVR agent types with correct format
  const routes = allRoutes.filter((route) => {
    // Show USER agent types
    if (route.agent_type === 'USER') {
      return true;
    }

    // Show IVR agent types that match the __IVR_NOTIFY_VM_{notify_id}_{greeting_id}__ pattern
    if (route.agent_type === 'IVR' && route.agent_id.startsWith('__IVR_NOTIFY_VM_')) {
      return true;
    }

    return false;
  });

  const handleAddRoute = () => {
    setEditingRoute(null);
    setIsAdding(true);
  };

  const handleEditRoute = (route: InboundRoute) => {
    setEditingRoute(route);
    setIsAdding(false);
  };

  const handleDeleteRoute = async (route: InboundRoute) => {
    setDeletingRouteId(route.route_id);
    try {
      await dispatch(deleteInboundRoute(route.route_id)).unwrap();
    } catch (error) {
      console.error('Failed to delete route:', error);
      alert('An error occurred while deleting the route');
    } finally {
      setDeletingRouteId(null);
    }
  };

  const handleSaveComplete = () => {
    setIsAdding(false);
    setEditingRoute(null);
  };

  const handleCancelEdit = () => {
    setIsAdding(false);
    setEditingRoute(null);
  };

  const handleToggleRouting = async () => {
    if (!team) return;

    const newValue = !team.has_routing_enabled;
    setIsTogglingRouting(true);

    try {
      const success = await getServicesManager().postSettings({
        is_team: true,
        has_routing_enabled: newValue
      });

      if (success) {
        // Update Redux state
        dispatch(updateTeam({
          ...team,
          has_routing_enabled: newValue
        }));
      } else {
        throw new Error('Update failed');
      }
    } catch (error) {
      console.error('Failed to toggle routing:', error);
      alert('Failed to update routing setting. Please try again.');
    } finally {
      setIsTogglingRouting(false);
    }
  };

  if (isLoading && routes.length === 0) {
    return (
      <div className="flex items-center justify-center h-full p-6">
        <Loader2 className="w-8 h-8 text-blue-600 animate-spin mr-3" />
        <Typography variant="mediumParagraph" color="#6B7280">
          Loading routes...
        </Typography>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 h-full overflow-auto">
      <div className="mb-6 flex items-start justify-between">
        <div>
          <Typography variant="h2" color="#1F2937" fontWeight={600}>
            Number Routing
          </Typography>
          <Typography variant="mediumParagraph" color="#6B7280" style={{ marginTop: '0.5rem' }}>
            Configure how incoming calls are routed for your purchased numbers
          </Typography>
        </div>

        {/* Routing Enable/Disable Toggle */}
        <div className="flex items-center gap-3 bg-white px-4 py-3 rounded-lg border border-gray-200 shadow-sm">
          <div className="text-right">
            <Typography variant="smallParagraph" color="#374151" fontWeight={500}>
              Routing
            </Typography>
            <Typography variant="smallParagraph" color={team?.has_routing_enabled ? '#059669' : '#9CA3AF'} style={{ marginTop: '0.125rem' }}>
              {team?.has_routing_enabled ? 'Enabled' : 'Disabled'}
            </Typography>
          </div>
          <button
            onClick={handleToggleRouting}
            disabled={isTogglingRouting}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 ${
              team?.has_routing_enabled ? 'bg-green-600' : 'bg-gray-300'
            }`}
            role="switch"
            aria-checked={!!team?.has_routing_enabled}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                team?.has_routing_enabled ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
          {isTogglingRouting && (
            <Loader2 size={16} className="text-gray-400 animate-spin" />
          )}
        </div>
      </div>

      {/* Show message if routing is disabled */}
      {!team?.has_routing_enabled && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Settings className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <Typography variant="h4" color="#1F2937" fontWeight={600} style={{ marginBottom: '0.5rem' }}>
                Routing is currently disabled
              </Typography>
              <Typography variant="mediumParagraph" color="#6B7280">
                Enable routing using the toggle above to start configuring phone number routes for your team.
              </Typography>
            </div>
          </div>
        </div>
      )}

      {/* Only show routing configuration if enabled */}
      {team?.has_routing_enabled && (
        <>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <Typography variant="mediumParagraph" color="#DC2626" fontWeight={500}>
            Error: {error}
          </Typography>
        </div>
      )}

      <div className="bg-white rounded-lg border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <Typography variant="h4" color="#1F2937" fontWeight={600}>
              Routing Rules ({routes.length})
            </Typography>
            <button
              onClick={handleAddRoute}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium text-sm transition-colors"
            >
              <Plus size={16} />
              <Typography variant="mediumParagraph" color="#FFFFFF" fontWeight={500}>
                Add Route
              </Typography>
            </button>
          </div>
        </div>

        {routes.length === 0 && !isAdding ? (
          <div className="text-center py-12 px-6">
            <Phone className="mx-auto mb-4 text-gray-400" size={48} />
            <Typography variant="h4" color="#6B7280" fontWeight={500}>
              No routing rules yet
            </Typography>
            <Typography variant="mediumParagraph" color="#9CA3AF" style={{ marginTop: '0.5rem' }}>
              Click &quot;Add Route&quot; to configure routing for your numbers
            </Typography>
          </div>
        ) : (
          <div className="overflow-visible">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left">
                    <Typography variant="smallCaption" color="#6B7280">
                      Number
                    </Typography>
                  </th>
                  <th className="px-6 py-3 text-left">
                    <Typography variant="smallCaption" color="#6B7280">
                      Route Type
                    </Typography>
                  </th>
                  <th className="px-6 py-3 text-left">
                    <Typography variant="smallCaption" color="#6B7280">
                      Target
                    </Typography>
                  </th>
                  <th className="px-6 py-3 text-left">
                    <Typography variant="smallCaption" color="#6B7280">
                      Greeting
                    </Typography>
                  </th>
                  <th className="px-6 py-3 text-right">
                    <Typography variant="smallCaption" color="#6B7280">
                      Actions
                    </Typography>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {/* Show add row at top if adding */}
                {isAdding && !editingRoute && (
                  <RouteInlineEditor
                    route={null}
                    onSave={handleSaveComplete}
                    onCancel={handleCancelEdit}
                  />
                )}

                {/* Existing Routes */}
                {routes.map((route) => {
                  // If editing this route, show editor in its place
                  if (editingRoute && editingRoute.route_id === route.route_id) {
                    return (
                      <RouteInlineEditor
                        key={route.route_id}
                        route={editingRoute}
                        onSave={handleSaveComplete}
                        onCancel={handleCancelEdit}
                      />
                    );
                  }

                  const isUserRoute = route.agent_type === 'USER';
                  const targetUser = isUserRoute ? allUsers.find(u => u.user_id === route.agent_id) : null;
                  const isDeleting = deletingRouteId === route.route_id;

                  // Parse greeting info and notify_id for team routes
                  let hasGreeting = false;
                  let targetName = team?.team_name || team?.team || 'Team';

                  if (!isUserRoute) {
                    const match = route.agent_id.match(/__IVR_NOTIFY_VM_([^_]+)_([^_]*)__/);
                    if (match) {
                      const notify_id = match[1];
                      const greeting_id = match[2];
                      hasGreeting = !!greeting_id;

                      // Check if notify_id is a subteam
                      const subteam = sortedUserGroupInfo.find(g => g.group.user_group_id === notify_id);
                      if (subteam) {
                        targetName = `Subteam: ${subteam.group.user_group_name}`;
                      } else if (notify_id !== team?.team_id) {
                        // It's some other ID we don't recognize
                        targetName = `Team (${notify_id.substring(0, 8)}...)`;
                      }
                    }
                  }

                  return (
                    <tr key={route.route_id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <Phone size={14} className="text-gray-400" />
                          <Typography variant="mediumParagraph" color="#1F2937">
                            {route.address || 'Unknown'}
                          </Typography>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          isUserRoute ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                        }`}>
                          <Typography variant="smallParagraph" color={isUserRoute ? '#7C3AED' : '#2563EB'} fontWeight={500}>
                            {isUserRoute ? 'User' : 'Team'}
                          </Typography>
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Typography variant="mediumParagraph" color="#6B7280">
                          {isUserRoute ? (targetUser?.user_name || route.agent_id) : targetName}
                        </Typography>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {!isUserRoute && (
                          <Typography variant="smallParagraph" color="#6B7280">
                            {hasGreeting ? 'Custom' : 'Default'}
                          </Typography>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <button
                          onClick={() => handleEditRoute(route)}
                          disabled={isDeleting || isAdding || editingRoute !== null}
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors mr-2 disabled:opacity-50"
                        >
                          <Edit2 size={14} />
                          <Typography variant="smallParagraph" color="#2563EB" fontWeight={500}>
                            Edit
                          </Typography>
                        </button>
                        <button
                          onClick={() => handleDeleteRoute(route)}
                          disabled={isDeleting || isAdding || editingRoute !== null}
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                        >
                          {isDeleting ? (
                            <Loader2 size={14} className="animate-spin" />
                          ) : (
                            <Trash2 size={14} />
                          )}
                          <Typography variant="smallParagraph" color="#DC2626" fontWeight={500}>
                            {isDeleting ? 'Deleting...' : 'Delete'}
                          </Typography>
                        </button>
                      </td>
                    </tr>
                  );
                })}

                {/* Add Route Row */}
                {!isAdding && !editingRoute && (
                  <RouteAdderRow onAdd={handleAddRoute} />
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
      </>
      )}
    </div>
  );
}
